import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { SolicitudRequerimiento } from '../types/solicitudRequerimiento.types';
import { SolicitudRequerimientoService } from '../services/solicitudRequerimientoService';

interface SolicitudRequerimientoState {
  items: SolicitudRequerimiento[];
  selectedItem: SolicitudRequerimiento | null;
  loading: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<void>;
  save: (data: SolicitudRequerimiento) => Promise<void>;
  remove: (id: number) => Promise<void>;
  setSelectedItem: (item: SolicitudRequerimiento | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const useSolicitudRequerimientoStore = create<SolicitudRequerimientoState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAll: async () => {
        set({ loading: true, error: null }, false, 'sr/fetchAll/start');
        try {
          const items = await SolicitudRequerimientoService.getAll();
          set({ items, loading: false }, false, 'sr/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar solicitudes' }, false, 'sr/fetchAll/error');
        }
      },

      fetchById: async (id: number) => {
        set({ loading: true, error: null }, false, 'sr/fetchById/start');
        try {
          const item = await SolicitudRequerimientoService.getById(id);
          set({ selectedItem: item, loading: false }, false, 'sr/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar solicitud' }, false, 'sr/fetchById/error');
        }
      },

      save: async (data: SolicitudRequerimiento) => {
        set({ loading: true, error: null }, false, 'sr/save/start');
        try {
          await SolicitudRequerimientoService.save(data);
          set({ loading: false }, false, 'sr/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar' }, false, 'sr/save/error');
          throw error;
        }
      },

      remove: async (id: number) => {
        set({ loading: true, error: null }, false, 'sr/delete/start');
        try {
          await SolicitudRequerimientoService.delete(id);
          set((state) => ({
            items: state.items.filter((i) => i.idSolicitudRequerimiento !== id),
            loading: false,
          }), false, 'sr/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' }, false, 'sr/delete/error');
        }
      },

      setSelectedItem: (item) => set({ selectedItem: item }, false, 'sr/setSelected'),
      clearError: () => set({ error: null }, false, 'sr/clearError'),
      reset: () => set(initialState, false, 'sr/reset'),
    }),
    { name: 'solicitud-requerimiento-store' }
  )
);
